import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, formatEUR } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Euro, TrendingUp, Car, Clock, Wrench, Users as UsersIcon, Bell, ShieldAlert, CalendarDays, FileText, ClipboardList, CheckCircle2, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useLang } from "@/i18n";
import PlateBadge from "@/components/PlateBadge";
import WorkcardPrintMenu from "@/components/WorkcardPrintMenu";

function KpiCard({ label, value, hint, icon: Icon, accent, testId, to }) {
  const body = (
    <Card data-testid={testId} className="card-hover border-border bg-card p-6 relative overflow-hidden h-full">
      <div className="flex items-start justify-between">
        <div className="space-y-1.5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="font-display text-3xl font-bold tabular-nums">{value}</div>
          {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
        </div>
        <div className={`h-10 w-10 rounded-md flex items-center justify-center border ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
  return to ? <Link to={to} className="block">{body}</Link> : body;
}

function SectionHead({ kicker, title, to, toLabel, icon: Icon, testId }) {
  return (
    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary">{kicker}</div>
        <h3 className="font-display text-xl font-bold">{title}</h3>
      </div>
      {to && (
        <Button asChild variant="outline" size="sm" className="rounded-full" data-testid={testId}>
          <Link to={to}>{Icon && <Icon className="h-4 w-4 mr-2" />}{toLabel}</Link>
        </Button>
      )}
    </div>
  );
}

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fmtDay = (iso) => { try { return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "2-digit" }); } catch { return ""; } };

export default function Dashboard() {
  const { t } = useLang();
  const { data: sum } = useQuery({ queryKey: ["dash"], queryFn: () => api.get("/dashboard/summary").then((r) => r.data), refetchInterval: 60000 });
  const { data: reminders = [] } = useQuery({ queryKey: ["reminders"], queryFn: () => api.get("/reminders").then((r) => r.data), refetchInterval: 60000 });
  const pendingReminders = reminders.filter((r) => r.status === "pending");
  const upcomingReminders = pendingReminders.slice().sort((a, b) => (a.due_date || "").localeCompare(b.due_date || "")).slice(0, 4);

  const s = sum || {};
  const API = process.env.REACT_APP_BACKEND_URL;
  const mechMin = s.mechanic_minutes_today || 0;
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="page-head flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-primary mb-2">{t("overviewLive")}</div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight">{t("workshopDashboard")}</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">{t("dashboardSubtitle")}</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" className="rounded-full" data-testid="quick-appointment">
            <Link to="/calendar"><CalendarDays className="h-4 w-4 mr-2" /> {t("newAppointment")}</Link>
          </Button>
          <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="quick-new-card">
            <Link to="/repairs?new=1"><Plus className="h-4 w-4 mr-2" /> {t("newJobCard")}</Link>
          </Button>
          <WorkcardPrintMenu mode="blank" size="default" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard testId="kpi-cars-open" to="/repairs" label={t("carsInWorkshop")} value={s.open_cars_count ?? 0}
          hint={`${s.open_cards_count ?? 0} ${t("dashOpenCards")}`} icon={Car}
          accent="bg-primary/15 border-primary/30 text-primary" />
        <KpiCard testId="kpi-completed-today" to="/repairs?tab=completed" label={t("dashReadyToday")} value={s.completed_today ?? 0}
          hint={t("dashReadyTodayHint")} icon={CheckCircle2}
          accent="bg-emerald-500/15 border-emerald-500/30 text-emerald-700 dark:text-emerald-400" />
        <KpiCard testId="kpi-appointments-today" to="/calendar" label={t("dashAppointmentsToday")} value={s.appointments_today_count ?? 0}
          hint={t("dashAppointmentsHint")} icon={CalendarDays}
          accent="bg-sky-500/15 border-sky-500/30 text-sky-700 dark:text-sky-400" />
        <KpiCard testId="kpi-mech-hours" label={t("dashHoursToday")} value={`${(mechMin / 60) | 0}h ${(mechMin % 60) | 0}m`}
          hint={`${(s.mechanic_hours_today || []).length} ${t("dashMechanicsActive")}`} icon={Clock}
          accent="bg-amber-500/20 border-amber-500/40 text-amber-700 dark:text-amber-400" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard testId="kpi-revenue-today" label={t("revenueToday")} value={formatEUR(s.revenue_today ?? 0)}
          hint={t("dashPaidInvoices")} icon={Euro}
          accent="bg-emerald-500/15 border-emerald-500/30 text-emerald-700 dark:text-emerald-400" />
        <KpiCard testId="kpi-revenue-week" label={t("revenueWeek")} value={formatEUR(s.revenue_week ?? 0)}
          hint={t("last7Days")} icon={TrendingUp}
          accent="bg-fuchsia-500/15 border-fuchsia-500/30 text-fuchsia-700 dark:text-fuchsia-400" />
        <KpiCard testId="kpi-revenue-month" label={t("revenueMonth")} value={formatEUR(s.revenue_month ?? 0)}
          hint={t("last30Days")} icon={TrendingUp}
          accent="bg-sky-500/15 border-sky-500/30 text-sky-700 dark:text-sky-400" />
        <KpiCard testId="kpi-unpaid" to="/invoices" label={t("dashUnpaid")} value={formatEUR(s.unpaid_total ?? 0)}
          hint={`${s.unpaid_count ?? 0} ${t("dashUnpaidHint")}`} icon={FileText}
          accent="bg-rose-500/15 border-rose-500/30 text-rose-700 dark:text-rose-400" />
      </div>

      {pendingReminders.length > 0 && (
        <Card className="p-6 border-border relative overflow-hidden" data-testid="dash-reminders">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent pointer-events-none" />
          <div className="relative flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-11 w-11 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-amber-700 dark:text-amber-400">{t("reminders")}</div>
                <div className="font-display text-2xl font-bold mt-0.5">{pendingReminders.length} {(t("pending") || "").toLowerCase()}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t("dashRemindersHint")}</div>
              </div>
            </div>
            <Button asChild variant="outline" className="rounded-full border-amber-500/40 hover:bg-amber-500/10" data-testid="dash-reminders-open">
              <Link to="/vehicles?tab=reminders"><Bell className="h-4 w-4 mr-2" /> {t("openReminders")}</Link>
            </Button>
          </div>
          <div className="relative mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {upcomingReminders.map((r) => (
              <Link key={r.id} to="/vehicles?tab=reminders" data-testid={`dash-reminder-${r.id}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-background/60 hover:border-amber-500/50 hover:bg-amber-500/5 transition-colors p-3">
                <div className="h-8 w-8 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center shrink-0">
                  {r.kind === "apk" ? <ShieldAlert className="h-4 w-4 text-rose-700 dark:text-rose-400" /> : <Bell className="h-4 w-4 text-amber-700 dark:text-amber-400" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{r.customer_name || "—"}</div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">{r.reason} · {r.due_date}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6 border-border" data-testid="open-cars-panel">
        <SectionHead kicker={t("liveWorkshop")} title={t("dashWorkshopNow")} to="/repairs" toLabel={t("jobCards")} icon={Wrench} testId="dash-goto-repairs" />
        {(s.open_cars || []).some((c) => c.hours_in_shop > 168) && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-rose-500/40 bg-rose-500/5 p-3 text-sm" data-testid="stale-cards-alert">
            <Clock className="h-4 w-4 text-rose-500 mt-0.5 shrink-0" />
            <div><span className="font-semibold text-rose-600">{(s.open_cars || []).filter((c) => c.hours_in_shop > 168).length} {t("staleCardsTitle")}</span> <span className="text-muted-foreground">— {t("staleCardHint")}</span></div>
          </div>
        )}
        {(s.open_cars || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center" data-testid="open-cars-empty">
            <Car className="h-8 w-8 mx-auto mb-2 opacity-40" />{t("dashNoOpenCars")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {(s.open_cars || []).map((c) => (
              <Link key={c.id} to={`/repairs?card=${encodeURIComponent(c.card_number)}`} data-testid={`open-car-${c.card_number}`}
                className="group flex gap-3 p-3 rounded-md border border-border bg-muted/30 hover:border-primary/50 transition-colors">
                <div className="h-20 w-20 rounded-md overflow-hidden bg-secondary shrink-0 flex items-center justify-center border border-border">
                  {c.cover_photo_id ? (
                    <img src={`${API}/api/photos/${c.cover_photo_id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : <Car className="h-8 w-8 text-muted-foreground" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-[10px] text-primary uppercase tracking-widest">{c.card_number}</div>
                    <Badge className={c.status === "in_progress"
                      ? "bg-amber-500/20 text-amber-700 dark:text-amber-400 border-amber-500/40"
                      : "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30"}>
                      {t(`repair_status_${c.status}`)}
                    </Badge>
                  </div>
                  <div className="font-display font-bold truncate">{[c.car_make, c.car_model].filter(Boolean).join(" ") || t("vehicleTbd")}</div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <PlateBadge plate={c.car_plate} country={c.car_country || "NL"} size="xs" />
                    {c.customer_name && <span className="text-[11px] font-mono text-muted-foreground truncate">{c.customer_name}</span>}
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-[11px] font-mono">
                    <span className={`inline-flex items-center gap-1 ${c.hours_in_shop > 168 ? "text-rose-500 font-semibold" : "text-muted-foreground"}`} title={c.hours_in_shop > 168 ? t("staleCardHint") : ""} data-testid={c.hours_in_shop > 168 ? `stale-card-${c.card_number}` : undefined}><Clock className="h-3 w-3" />{c.hours_in_shop > 48 ? `${Math.floor(c.hours_in_shop / 24)} d` : `${c.hours_in_shop}h`}</span>
                    {c.mechanic_name && <><span className="text-muted-foreground">·</span><span className="text-muted-foreground truncate">{c.mechanic_name}</span></>}
                    <span className="text-muted-foreground">·</span>
                    <span className="tabular-nums">{formatEUR(c.grand_total || 0)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-6 border-border" data-testid="dash-appointments">
          <SectionHead kicker={t("dashTodayTomorrow")} title={t("appointmentsTitle")} to="/calendar" toLabel={t("agenda")} icon={CalendarDays} testId="dash-goto-calendar" />
          {(s.appointments || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="appointments-empty">{t("dashNoAppointments")}</div>
          ) : (
            <div className="space-y-2">
              {(s.appointments || []).map((a) => (
                <Link key={a.id} to="/calendar" data-testid={`dash-appt-${a.id}`} className="flex items-center gap-3 p-3 rounded-md border border-border bg-muted/30 hover:border-primary/50 transition-colors">
                  <div className="text-center shrink-0 w-14">
                    <div className="font-mono text-sm font-bold">{fmtTime(a.scheduled_at)}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{a.scheduled_at?.slice(0, 10) === todayStr ? t("today") : fmtDay(a.scheduled_at)}</div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{a.customer_name || "—"} <span className="text-muted-foreground font-normal">· {a.service_type}</span></div>
                    <div className="flex items-center gap-2 text-[11px] font-mono text-muted-foreground">
                      {a.car_plate && <PlateBadge plate={a.car_plate} country="NL" size="xs" />}
                      <span className="truncate">{a.vehicle_label}</span>
                      {a.mechanic_name && <span>· {a.mechanic_name}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border-border" data-testid="dash-unpaid">
          <SectionHead kicker={t("dashToCollect")} title={t("dashUnpaid")} to="/invoices" toLabel={t("invoices")} icon={FileText} testId="dash-goto-invoices" />
          {(s.unpaid_invoices || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="unpaid-empty">{t("dashNoUnpaid")}</div>
          ) : (
            <div className="space-y-2">
              {(s.unpaid_invoices || []).map((i) => (
                <Link key={i.id} to="/invoices" data-testid={`dash-unpaid-${i.invoice_number}`} className="flex items-center justify-between gap-3 p-3 rounded-md border border-border bg-muted/30 hover:border-primary/50 transition-colors">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{i.customer_name || "—"}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{i.invoice_number}{i.due_date && ` · ${i.due_date}`}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono tabular-nums font-semibold">{formatEUR(i.total || 0)}</div>
                    {i.overdue && <Badge className="bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30 text-[10px]">{t("overdue")}</Badge>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="xl:col-span-2 p-6 border-border" data-testid="dash-weekly-revenue">
          <SectionHead kicker={t("dashLast8Weeks")} title={t("dashRevenuePerWeek")} />
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.weekly_revenue || []}>
                <CartesianGrid stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="week" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip formatter={(v) => formatEUR(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
                <Bar dataKey="revenue" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-border" data-testid="mech-hours-panel">
          <div className="flex items-center gap-2 mb-3">
            <UsersIcon className="h-5 w-5 text-primary" />
            <h3 className="font-display text-xl font-bold">{t("mechanicHoursToday")}</h3>
          </div>
          {(s.mechanic_hours_today || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">{t("dashNoHoursYet")}</div>
          ) : (
            <div className="space-y-2">
              {(s.mechanic_hours_today || []).map((m) => (
                <div key={m.name} className="p-3 rounded-md border border-border bg-muted/30 flex items-center justify-between">
                  <span className="font-medium flex items-center gap-2"><ClipboardList className="h-4 w-4 text-muted-foreground" />{m.name}</span>
                  <span className="font-mono tabular-nums text-primary">{m.hours}h</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
